/**
 * Privacy mode (the owner, 2026-10-11; ADRs 0144 and 0145): made-up client
 * data, for showing every screen to someone outside Axon, and every feature.
 *
 * Turning it on makes a demo copy of the data on the server; while it's on,
 * each API request names that copy, and is served from it with the client data
 * made up. Everything works there, Log included, and nothing reaches Autotask
 * or the real data. It stays on in this browser until it's clicked off,
 * through a reload (the owner's choice), and turning it off drops the copy.
 *
 * Kept here: null (off), the copy's ID, or "on" (on, with no copy yet: one
 * kept before ADR 0145, or whose copy is being made again).
 */
import { useSyncExternalStore } from "react";

export const PRIVACY_HEADER = "X-Privacy-Mode";
export const PRIVACY_CHANGED = "Privacy mode was turned on or off: this loads again.";
const KEY = "axon-time.privacy-mode";
const DEMO_ID = /^demo_[0-9a-f]{32}$/;

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function stored(): string | null {
  try {
    const value = storage()?.getItem(KEY) ?? null;
    return value === "on" || (value !== null && DEMO_ID.test(value)) ? value : null;
  } catch {
    return null;
  }
}

let value = stored();
const listeners = new Set<() => void>();

export function isPrivacyOn(): boolean {
  return value !== null;
}

/** The demo copy privacy mode is on, if it has one yet. */
export function privacyDemo(): string | null {
  return value !== null && DEMO_ID.test(value) ? value : null;
}

/** Off (null), on with this demo copy, or on with none yet ("on"). */
export function setPrivacy(next: string | null): void {
  value = next;
  try {
    if (next === null) {
      storage()?.removeItem(KEY);
    } else {
      storage()?.setItem(KEY, next);
    }
  } catch {
    // Not remembered: it's still on, or off, for this page.
  }
  notify();
}

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

// Another tab turned it on or off: this one follows, so a tab opened before a
// demo never shows the real data during it.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== KEY && event.key !== null) {
      return;
    }
    const next = stored();
    if (next !== value) {
      value = next;
      notify();
    }
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePrivacy(): boolean {
  return useSyncExternalStore(subscribe, isPrivacyOn);
}

/** What the API client needs of privacy mode: swapped for a fake in tests. */
export interface PrivacyStore {
  /** Null when off; else the demo copy, or "on" when there's none yet. */
  get(): string | null;
  set(next: string | null): void;
}

export const privacyStore: PrivacyStore = { get: () => value, set: setPrivacy };
